import { z } from "zod";
import type { SalesPrices, SalesSeller } from "./sales.js";
import { computeQuote, VAT_RATE, type SalesQuoteInput, type SalesQuoteLine } from "./salesQuote.js";

/**
 * Facturatie van Klantpunt zelf (Platformbeheer › Facturatie, docs/VERKOOP.md stap 4): per maand een
 * factuur per overeenkomst, vooraf, met het PO-nummer of kenmerk en bewaking van het PO-budget.
 */

/** Betaaltermijn in dagen (zoals in de voorwaarden van de offerte). */
export const SALES_PAYMENT_DAYS = 14;
/** Seintje als het PO-budget na de factuur voor minstens dit deel gebruikt is. */
export const PO_BUDGET_WARN = 0.8;

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Ongeldige datum");
const text = (max: number) => z.string().trim().max(max);

/** Maand als `2026-12`. */
export const periodSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Ongeldige maand");

/** Eerste dag van de maand: `2026-12` → `2026-12-01`. */
export const periodStart = (period: string) => `${period}-01`;
/** Laatste dag van de maand. */
export function periodEnd(period: string) {
  const [y, m] = period.split("-").map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}
/** `2026-12-15` → `2026-12`. */
export const billingPeriodOf = (iso: string) => iso.slice(0, 7);
/** Aantal maanden van a naar b (beide `YYYY-MM`). */
export function monthsBetween(a: string, b: string) {
  const [ay, am] = a.split("-").map(Number) as [number, number];
  const [by, bm] = b.split("-").map(Number) as [number, number];
  return (by - ay) * 12 + (bm - am);
}
/** "december 2026" */
export const periodLabel = (period: string) =>
  new Date(`${period}-15T12:00:00Z`).toLocaleDateString("nl-NL", { month: "long", year: "numeric" });

export type SalesInvoiceLine = SalesQuoteLine;

export interface SalesInvoiceAmounts {
  lines: SalesInvoiceLine[];
  /** Excl. btw. */
  subtotal: number;
  vat: number;
  total: number;
}

/**
 * Regels voor één maand van een overeenkomst. De korting geldt in de eerste `discountMonths` maanden
 * (0 = altijd); eenmalige regels (inrichting) komen op de eerste factuur.
 */
export function invoiceLines(
  input: SalesQuoteInput,
  prices: SalesPrices,
  opts: { monthIndex: number; first: boolean },
): SalesInvoiceAmounts {
  const t = computeQuote(input, prices);
  const lines: SalesInvoiceLine[] = [...t.monthlyLines];
  if (t.monthlyDiscount && (input.discountMonths === 0 || opts.monthIndex < input.discountMonths))
    lines.push(t.monthlyDiscount);
  if (opts.first) lines.push(...t.onceLines);
  const subtotal = lines.reduce((s, l) => s + l.total, 0);
  const vat = Math.round(subtotal * VAT_RATE);
  return { lines, subtotal, vat, total: subtotal + vat };
}

/** Stand van het PO-budget na deze factuur. */
export type PoBudgetState = "none" | "ok" | "low" | "over" | "expired";

export function poBudgetState(opts: {
  noPo: boolean;
  budget: number | null;
  until: string | null;
  used: number;
  amount: number;
  /** Eerste dag van de te factureren maand. */
  on: string;
}): PoBudgetState {
  if (opts.noPo) return "none";
  if (opts.until && opts.until < opts.on) return "expired";
  if (opts.budget === null) return "none";
  const after = opts.used + opts.amount;
  if (after > opts.budget) return "over";
  return after >= opts.budget * PO_BUDGET_WARN ? "low" : "ok";
}

/** Stand van een overeenkomst in de maandronde. */
export type BillingRowState =
  /** Klaar om te factureren. */
  | "ready"
  /** Klaar, maar het PO-budget is daarna voor 80% of meer gebruikt of het PO loopt binnen een maand af. */
  | "low"
  /** Wordt tegengehouden: het budget is op of het PO is verlopen. Bewust doorzetten kan. */
  | "blocked"
  /** Voor deze maand is al gefactureerd. */
  | "done"
  /** Ingangsdatum ligt na deze maand. */
  | "later"
  /** Opgezegd voor deze maand. */
  | "ended";

export interface BillingRoundRow {
  agreementId: string;
  customerId: string;
  customerName: string;
  quoteNumber: string;
  state: BillingRowState;
  /** Waarom tegengehouden of let op, in gewone taal. */
  reason: string | null;
  summary: string;
  noPo: boolean;
  poNumber: string;
  reference: string;
  poBudget: number | null;
  poUntil: string | null;
  /** Al gefactureerd op dit PO (excl. btw). */
  poUsed: number;
  amounts: SalesInvoiceAmounts;
  /** Bestaande factuur voor deze maand. */
  invoiceNumber: string | null;
}

export interface BillingRoundDto {
  period: string;
  rows: BillingRoundRow[];
  /** Openstaand (incl. btw) en waarvan te laat. */
  outstanding: number;
  overdue: number;
  /** Lopende overeenkomsten per maand (excl. btw, zonder tijdelijke korting). */
  mrr: number;
}

export const billingRoundRunSchema = z.object({
  period: periodSchema,
  agreementIds: z.array(z.uuid()).min(1).max(500),
  /** Tegengehouden overeenkomsten die toch gefactureerd worden. */
  force: z.array(z.uuid()).max(500).default([]),
});
export type BillingRoundRunBody = z.input<typeof billingRoundRunSchema>;

export const SALES_INVOICE_STATUSES = ["open", "paid"] as const;
export type SalesInvoiceStatus = (typeof SALES_INVOICE_STATUSES)[number];
export const SALES_INVOICE_STATUS_LABELS: Record<SalesInvoiceStatus | "overdue", string> = {
  open: "Open",
  paid: "Betaald",
  overdue: "Te laat",
};
export const invoiceDisplayStatus = (status: SalesInvoiceStatus, dueOn: string, today: string) =>
  status === "open" && dueOn < today ? ("overdue" as const) : status;

export interface SalesInvoiceRow {
  id: string;
  number: string;
  agreementId: string;
  customerId: string;
  customerName: string;
  period: string;
  issuedOn: string;
  dueOn: string;
  poNumber: string;
  reference: string;
  subtotal: number;
  vat: number;
  total: number;
  status: SalesInvoiceStatus;
  paidOn: string | null;
  /** Bewust gefactureerd terwijl het PO-budget op of het PO verlopen was. */
  forced: boolean;
}

export interface SalesInvoiceBillTo {
  name: string;
  address: string;
  email: string;
  attention: string;
}

export interface SalesInvoiceDetail extends SalesInvoiceRow {
  lines: SalesInvoiceLine[];
  billTo: SalesInvoiceBillTo;
  seller: SalesSeller;
  quoteNumber: string;
}

export const salesInvoiceUpdateSchema = z.object({
  status: z.enum(SALES_INVOICE_STATUSES),
  paidOn: isoDate.nullable().optional(),
});
export type SalesInvoiceUpdateBody = z.input<typeof salesInvoiceUpdateSchema>;

/** Overeenkomst in de lijst Overeenkomsten. */
export interface SalesAgreementRow {
  id: string;
  quoteId: string;
  quoteNumber: string;
  customerId: string;
  customerName: string;
  signerName: string;
  signerRole: string;
  acceptedAt: string;
  startOn: string | null;
  termMonths: number;
  endsOn: string | null;
  monthly: number;
  discountPct: number;
  discountMonths: number;
  noPo: boolean;
  poNumber: string;
  reference: string;
  poBudget: number | null;
  poUntil: string | null;
  poUsed: number;
  iban: string;
  invoiceEmail: string;
  billingAddress: string;
  invoiceCount: number;
}

/** PO, factuurgegevens of opzeggen wijzigen. */
export const salesAgreementUpdateSchema = z
  .object({
    noPo: z.boolean(),
    poNumber: text(60).default(""),
    reference: text(100).default(""),
    poBudget: z.number().int().min(0).max(2_000_000_000).nullable().default(null),
    poUntil: isoDate.nullable().default(null),
    invoiceEmail: z.union([z.email("Vul een geldig e-mailadres in").max(200), z.literal("")]).default(""),
    billingAddress: text(400).default(""),
    /** Laatste dag waarover gefactureerd wordt; leeg = loopt door. */
    endsOn: isoDate.nullable().default(null),
  })
  .superRefine((v, ctx) => {
    if (!v.noPo && v.poNumber.length < 2)
      ctx.addIssue({ code: "custom", path: ["poNumber"], message: "Vul het PO-nummer in" });
  });
export type SalesAgreementUpdateBody = z.input<typeof salesAgreementUpdateSchema>;
